// React components
import { useEffect, useState } from "react";

// Material UI components
import { Autocomplete, TextField } from "@mui/material";

// Types
import { ISearchSelectProps, TypeOfList } from "./CommonSearchSelectTypes";
import { IDictionary, IGenericObject } from "../commonTypes/commonTypes";

// Formik functions
import { getIn } from "formik";

// Search select component
const CommonSearchSelect: React.FC<ISearchSelectProps> = ({
  list,
  formik,
  label,
  currentValue,
  formPath,
  width,
  type,
  sx,
  disabled,
  isObjectDataRequired,
  readOnlyInput,
  loading,
  getOptionColor,
}) => {
  // Set provonce list on the basis of selected language
  const [mappedList, setMappedList] = useState<IGenericObject[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>(""); // State to store the search term

  useEffect(() => {
    if (!list) return;

    const sortByName = (a: { name: string }, b: { name: string }) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    };

    if (type === TypeOfList.ArrayOfObject) {
      const sorted = (list as IGenericObject[]).slice().sort(sortByName);
      setMappedList(sorted);
    } else {
      // Get the current language
      const selectedLanguage = "en"; // or "th"

      // Map the selected language list into the desired format
      const updatedObjArray = (list as IDictionary)?.[selectedLanguage] ?? [];

      const sorted = [...updatedObjArray].sort(sortByName);
      setMappedList(sorted);
    }
  }, [list, type]);

  useEffect(() => {
    /* eslint-disable no-empty-function */
  }, [setMappedList]);
  // Filtering and sorting based on the search term
  const filterAndSortOptions = (
    options: IGenericObject[],
    searchTerm: string,
  ) => {
    return [...options].sort((a, b) => {
      const startsWithA = a.name
        .toLowerCase()
        .startsWith(searchTerm.toLowerCase());
      const startsWithB = b.name
        .toLowerCase()
        .startsWith(searchTerm.toLowerCase());

      if (startsWithA && !startsWithB) return -1; // a comes first if it matches the prefix
      if (!startsWithA && startsWithB) return 1; // b comes first if it matches the prefix

      return 0; // If both or neither match, maintain original order
    });
  };

  // Currently-selected option, shared by the Autocomplete `value` prop and
  // (when `getOptionColor` is supplied) the selected-value text colour.
  const selectedOption: IGenericObject | null =
    mappedList?.find((item: IGenericObject) => {
      if (
        isObjectDataRequired &&
        typeof currentValue === "object" &&
        currentValue !== null
      ) {
        return item.id === currentValue.id;
      } else {
        return item.id === currentValue;
      }
    }) || null;
  const selectedOptionColor = selectedOption
    ? getOptionColor?.(selectedOption.id)
    : undefined;

  return (
    <>
      <Autocomplete
        options={filterAndSortOptions(mappedList, searchTerm)} // Use the filtered and sorted list here
        getOptionLabel={(option) => option.name}
        sx={sx ? { ...sx } : null}
        disabled={disabled} // <-- Pass the disabled prop here
        // While the parent is still fetching the backing lookup, show
        // MUI's built-in "Loading…" indicator instead of the empty-state
        // "No options" text — avoids the misleading flash users would
        // otherwise see if they open the dropdown before the fetch
        // completes (e.g. the Dealer dropdown on Add/Edit Field).
        loading={!!loading}
        value={selectedOption}
        onChange={async (event, value: IGenericObject | null) => {
          if (isObjectDataRequired) {
            await formik.setFieldValue(formPath, value);
          } else {
            await formik.setFieldValue(formPath, value ? value.id : "");
          }
        }}
        onInputChange={(event, newInputValue) => {
          setSearchTerm(newInputValue); // Update the search term as user types
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="filled"
            size="small"
            fullWidth // Keeps consistency, but width is controlled by the parent
            // When readOnlyInput is set, make the native input read-only so
            // the user can only pick from the dropdown (no free typing).
            inputProps={{
              ...params.inputProps,
              readOnly: readOnlyInput ? true : params.inputProps?.readOnly,
            }}
            // AA-1733 — colour the selected value's text to match the
            // Compliance Report colouring when `getOptionColor` is supplied.
            // Leaves the default input text colour untouched otherwise.
            sx={
              selectedOptionColor
                ? { "& .MuiInputBase-input": { color: selectedOptionColor } }
                : undefined
            }
            error={
              getIn(formik.touched, formPath) &&
              Boolean(getIn(formik.errors, formPath))
            } // Show error if touched
            helperText={
              getIn(formik.touched, formPath) && getIn(formik.errors, formPath)
            } // Show helper text for error
          />
        )}
        renderOption={(props, option) => {
          // AA-1733 — colour this option's label to match the Compliance
          // Report colouring when `getOptionColor` is supplied (EUDR filter
          // dropdowns only); every other usage keeps the default colour.
          const optionColor = getOptionColor?.(option.id);
          return (
            <li
              {...props}
              key={option.id}
              style={optionColor ? { color: optionColor } : undefined}
            >
              {option.name}
            </li>
          );
        }}
        className="search-select-component"
      />
    </>
  );
};

export default CommonSearchSelect;
